import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, Loader2, Star } from 'lucide-react';
import {
  fetchTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  resolveImageUrl,
  formatApiError,
} from '../../services/api';
import ImageUploader from './ImageUploader';

const emptyTestimonial = { name: '', location: '', rating: 5, text: '', image: '', order: 0 };

const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTestimonial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchTestimonials();
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing('new');
    setForm({ ...emptyTestimonial, order: items.length + 1 });
    setError('');
  };

  const startEdit = (t) => {
    setEditing(t.id);
    setForm({
      name: t.name || '',
      location: t.location || '',
      rating: t.rating || 5,
      text: t.text || '',
      image: t.image || '',
      order: t.order || 0,
    });
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyTestimonial);
    setError('');
  };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) {
      setError('Name and Testimonial text are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        location: form.location.trim(),
        rating: Math.max(1, Math.min(5, parseInt(form.rating) || 5)),
        text: form.text.trim(),
        image: form.image || '',
        order: parseInt(form.order) || 0,
      };
      if (editing === 'new') {
        await createTestimonial(payload);
      } else {
        await updateTestimonial(editing, payload);
      }
      await load();
      cancel();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial? This cannot be undone.')) return;
    try {
      await deleteTestimonial(id);
      await load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Testimonials</h2>
          <p className="text-amber-100/60 text-sm mt-1">{items.length} testimonial{items.length !== 1 ? 's' : ''} — shown on Home and /testimonials</p>
        </div>
        {!editing ? (
          <Button onClick={startCreate} className="btn-gold" data-testid="new-testimonial-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Testimonial
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="mb-8 p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg space-y-4">
          <h3 className="text-xl font-semibold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>
            {editing === 'new' ? 'Add Testimonial' : 'Edit Testimonial'}
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Priya S."
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="testimonial-name-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Location</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Mumbai"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
                data-testid="testimonial-location-input"
              />
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Rating (1–5)</Label>
              <div className="flex gap-1 mt-2" data-testid="testimonial-rating-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm({ ...form, rating: n })}
                    className="p-1"
                  >
                    <Star
                      className={`w-7 h-7 transition ${
                        n <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-amber-100/30'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-amber-300 text-xs uppercase tracking-wider">Display Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                placeholder="1"
                className="bg-emerald-950/60 border-amber-600/30 text-amber-100"
              />
              <p className="text-xs text-amber-100/50 mt-1">Lower numbers appear first</p>
            </div>
          </div>

          <div>
            <Label className="text-amber-300 text-xs uppercase tracking-wider">Testimonial Text *</Label>
            <Textarea
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              placeholder="Meeta's reading was incredibly accurate and insightful..."
              rows={5}
              className="bg-emerald-950/60 border-amber-600/30 text-amber-100 font-serif-mystical"
              data-testid="testimonial-text-input"
            />
          </div>

          <ImageUploader
            value={form.image}
            onChange={(url) => setForm({ ...form, image: url })}
            label="Client Photo (optional)"
          />

          {error ? <div className="p-3 rounded bg-red-950/60 border border-red-500/40 text-red-300 text-sm">{error}</div> : null}

          <div className="flex gap-3 pt-2">
            <Button onClick={save} disabled={saving} className="btn-gold" data-testid="testimonial-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save Testimonial'}
            </Button>
            <Button onClick={cancel} className="btn-mystical" data-testid="testimonial-cancel-btn">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <p className="text-amber-100/60 text-center py-12">Loading testimonials...</p>
      ) : items.length === 0 ? (
        <p className="text-amber-100/60 text-center py-12">No testimonials yet. Click "New Testimonial" to add one.</p>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div
              key={t.id}
              className="flex items-start gap-4 p-4 bg-emerald-950/30 border border-amber-600/20 rounded-lg hover:border-amber-500/50 transition"
              data-testid={`testimonial-row-${t.id}`}
            >
              {t.image ? (
                <img
                  src={resolveImageUrl(t.image)}
                  alt={t.name}
                  className="w-16 h-16 rounded-full object-cover border border-amber-600/30 flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-emerald-900/60 border border-amber-600/30 flex items-center justify-center flex-shrink-0 text-amber-300 font-bold text-xl" style={{ fontFamily: 'Cinzel, serif' }}>
                  {(t.name || '?')[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-amber-200 font-semibold" style={{ fontFamily: 'Cinzel, serif' }}>{t.name}</h3>
                  {t.location ? <span className="text-xs text-amber-100/60">· {t.location}</span> : null}
                  <span className="text-xs text-amber-100/40">Order: {t.order ?? 0}</span>
                </div>
                <div className="flex gap-0.5 my-1">
                  {[...Array(t.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-sm text-amber-100/80 italic line-clamp-2 font-serif-mystical">"{t.text}"</p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" onClick={() => startEdit(t)} className="btn-mystical" data-testid={`edit-testimonial-${t.id}`}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(t.id)}
                  className="bg-red-900/60 text-red-300 hover:bg-red-900 border border-red-500/30"
                  data-testid={`delete-testimonial-${t.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager;
